import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';

import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { BackButton } from '@components/ui/back-button/back-button';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { SaveButton } from '@components/button/save-button/save-button/save-button';
import {
  DataTableComponent,
  TableColumn,
  TableConfig,
} from '@components/data-table/data-table.component';
import { TableActionsComponent } from '@components/table-actions/table-actions.component';
import { MsfInput } from '@components/ui/forms/input/input';
import { MsfSelect } from '@components/select/select';
import { MobileSettingsService } from '@6grain/masofa-monolith-api-client/common/api/mobileSettings.service';
import { MobileSettings } from '@6grain/masofa-monolith-api-client/common/model/mobileSettings';

/** Platform values accepted by common/AppVersion/Check, doubling as configuration keys. */
const PLATFORMS = ['ANDROID', 'IOS'] as const;

/**
 * Numeric core with an optional pre-release / build suffix. Mirrors AppVersionParser.TryParseSemVer,
 * which cuts the string at the first '+' and then at the first '-' and accepts 1..3 numeric parts.
 */
const SEMVER_PATTERN = /^\d+(\.\d+){0,2}([-+][0-9A-Za-z.-]*)?$/;

/** Store links are not always https - Play Market and App Store also use their own schemes. */
const STORE_URL_PATTERN = /^(https?|market|itms-apps|itms-appss):\/\/\S+$/i;

interface MobileSettingsForm {
  id?: string;
  platform: string;
  latestVersion: string;
  minSupportedVersion: string;
  storeUrl: string;
}

/** Row view-model: `_misconfigured` is precomputed on load - no method calls from the template. */
type MobileSettingsRow = MobileSettings & { _misconfigured: boolean };

type VersionTriple = [number, number, number];

@Component({
  selector: 'app-mobile-settings-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    TooltipModule,
    ToastModule,
    TranslateModule,
    BackButton,
    PrimaryButton,
    SecondaryButton,
    SaveButton,
    DataTableComponent,
    TableActionsComponent,
    MsfInput,
    MsfSelect,
  ],
  providers: [MessageService],
  templateUrl: './mobile-settings-page.html',
  styleUrl: './mobile-settings-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileSettingsPageComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly mobileSettingsService = inject(MobileSettingsService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);

  readonly items = signal<MobileSettingsRow[]>([]);
  readonly loading = signal<boolean>(true);
  readonly saving = signal<boolean>(false);
  readonly deleting = signal<boolean>(false);
  readonly loadError = signal<string | null>(null);

  readonly dialogVisible = signal<boolean>(false);
  readonly dialogMode = signal<'create' | 'edit'>('create');
  readonly deleteModalVisible = signal<boolean>(false);
  readonly submitted = signal<boolean>(false);

  form: MobileSettingsForm = this.emptyForm();
  itemToDelete: MobileSettingsRow | null = null;

  /**
   * Bumped on every form edit so the validation computeds re-run - `form` stays a plain
   * object because `msf-input` / `msf-select` bind to it via `[(model)]`.
   */
  private readonly formVersion = signal(0);

  private readonly platformTemplate = viewChild<TemplateRef<unknown>>('platformTemplate');
  private readonly minVersionTemplate = viewChild<TemplateRef<unknown>>('minVersionTemplate');
  private readonly storeUrlTemplate = viewChild<TemplateRef<unknown>>('storeUrlTemplate');
  private readonly rowActionsTemplate = viewChild<TemplateRef<unknown>>('rowActionsTemplate');

  readonly tableColumns = computed<TableColumn[]>(() => [
    {
      field: 'platform',
      header: 'adminMobileSettings.platform',
      type: 'custom',
      customTemplate: this.platformTemplate(),
    },
    { field: 'latestVersion', header: 'adminMobileSettings.latestVersion', type: 'text' },
    {
      field: 'minSupportedVersion',
      header: 'adminMobileSettings.minSupportedVersion',
      type: 'custom',
      customTemplate: this.minVersionTemplate(),
    },
    {
      field: 'storeUrl',
      header: 'adminMobileSettings.storeUrl',
      type: 'custom',
      customTemplate: this.storeUrlTemplate(),
    },
    { field: 'lastUpdateAt', header: 'adminMobileSettings.lastUpdateAt', type: 'date' },
    {
      field: 'actions',
      header: 'Actions',
      type: 'custom',
      customTemplate: this.rowActionsTemplate(),
    },
  ]);

  readonly tableConfig: TableConfig = {
    paginator: false,
    keepOrder: true,
    emptyMessage: 'adminMobileSettings.empty',
  };

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.loading.set(true);
    this.loadError.set(null);

    this.mobileSettingsService
      .commonMobileSettingsGetByQueryPost({ offset: 0, take: 100, filters: [] })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const sorted = [...(response ?? [])]
            .sort((a, b) => (a.platform ?? '').localeCompare(b.platform ?? ''))
            .map((item) => ({
              ...item,
              _misconfigured: this.isGateInverted(item.minSupportedVersion, item.latestVersion),
            }));
          this.items.set(sorted);
          this.loading.set(false);
        },
        error: (error: HttpErrorResponse) => {
          this.items.set([]);
          this.loadError.set(this.formatError(error));
          this.loading.set(false);
        },
      });
  }

  // ---------------------------------------------------------------- dialog

  /**
   * Platforms that have no row yet - a platform may be configured only once.
   * Only used when creating: an existing row is bound to its platform.
   *
   * Must be a computed and not a getter: p-select re-initialises whenever the
   * [options] reference changes, and a getter hands it a fresh array on every
   * change detection pass, which wipes the picked value.
   */
  readonly availablePlatforms = computed<{ label: string; value: string }[]>(() => {
    const taken = new Set(this.items().map((item) => (item.platform ?? '').toUpperCase()));

    return PLATFORMS.filter((platform) => !taken.has(platform)).map((platform) => ({
      label: platform,
      value: platform,
    }));
  });

  readonly canAdd = computed<boolean>(() => this.availablePlatforms().length > 0);

  openCreateDialog(): void {
    if (!this.canAdd()) {
      return;
    }

    this.form = this.emptyForm();
    this.form.platform = this.availablePlatforms()[0]?.value ?? '';
    this.submitted.set(false);
    this.dialogMode.set('create');
    this.dialogVisible.set(true);
  }

  openEditDialog(item: MobileSettingsRow): void {
    this.form = {
      id: item.id,
      platform: (item.platform ?? '').toUpperCase(),
      latestVersion: item.latestVersion ?? '',
      minSupportedVersion: item.minSupportedVersion ?? '',
      storeUrl: item.storeUrl ?? '',
    };
    this.submitted.set(false);
    this.dialogMode.set('edit');
    this.dialogVisible.set(true);
  }

  closeDialog(): void {
    this.dialogVisible.set(false);
    this.submitted.set(false);
  }

  /** The dialog also closes itself on Esc and on the close icon - keep the signal in sync. */
  onDialogVisibleChange(visible: boolean): void {
    if (!visible) {
      this.closeDialog();
    }
  }

  /** Keeps the validation computeds in sync with the two-way-bound plain `form` object. */
  onFormChange(): void {
    this.formVersion.update((version) => version + 1);
  }

  // ------------------------------------------------------------ validation

  readonly platformError = computed<string | null>(() => {
    this.formVersion();
    return this.form.platform ? null : 'adminMobileSettings.errors.platformRequired';
  });

  readonly latestVersionError = computed<string | null>(() => {
    this.formVersion();
    return this.versionError(this.form.latestVersion);
  });

  readonly minSupportedVersionError = computed<string | null>(() => {
    this.formVersion();
    return this.versionError(this.form.minSupportedVersion);
  });

  readonly storeUrlError = computed<string | null>(() => {
    this.formVersion();
    const value = this.form.storeUrl.trim();
    if (value && !STORE_URL_PATTERN.test(value)) {
      return 'adminMobileSettings.errors.storeUrlInvalid';
    }
    return null;
  });

  /**
   * A minimum above the latest version force-updates every user, including those already
   * running the newest build. Worth a warning, but not blocking - it is a legitimate way
   * to pull an entire release from circulation.
   */
  readonly gateWarning = computed<boolean>(() => {
    this.formVersion();
    return this.isGateInverted(this.form.minSupportedVersion, this.form.latestVersion);
  });

  readonly formValid = computed<boolean>(
    () =>
      !this.platformError() &&
      !this.latestVersionError() &&
      !this.minSupportedVersionError() &&
      !this.storeUrlError(),
  );

  private versionError(value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed) {
      return 'adminMobileSettings.errors.versionRequired';
    }
    if (!SEMVER_PATTERN.test(trimmed)) {
      return 'adminMobileSettings.errors.versionInvalid';
    }
    return null;
  }

  // ---------------------------------------------------------------- saving

  save(): void {
    this.submitted.set(true);

    if (!this.formValid() || this.saving()) {
      return;
    }

    const source = this.items().find((item) => item.id === this.form.id);
    const model: MobileSettings = {
      ...(source ?? {}),
      platform: this.form.platform,
      latestVersion: this.form.latestVersion.trim(),
      minSupportedVersion: this.form.minSupportedVersion.trim(),
      storeUrl: this.form.storeUrl.trim() || null,
    };

    this.saving.set(true);

    // Create returns the new id, Update returns the model - neither is used, only the outcome.
    const request$: Observable<unknown> =
      this.dialogMode() === 'edit'
        ? this.mobileSettingsService.commonMobileSettingsUpdatePut(model)
        : this.mobileSettingsService.commonMobileSettingsCreatePost(model);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.saving.set(false);
        this.dialogVisible.set(false);
        this.submitted.set(false);
        this.showSuccess('adminMobileSettings.saved');
        this.loadData();
      },
      error: (error: HttpErrorResponse) => {
        this.saving.set(false);
        this.showError('adminMobileSettings.saveFailed', this.formatError(error));
      },
    });
  }

  // -------------------------------------------------------------- deleting

  askDelete(item: MobileSettingsRow): void {
    this.itemToDelete = item;
    this.deleteModalVisible.set(true);
  }

  cancelDelete(): void {
    this.deleteModalVisible.set(false);
    this.itemToDelete = null;
  }

  onDeleteModalVisibleChange(visible: boolean): void {
    if (!visible) {
      this.cancelDelete();
    }
  }

  confirmDelete(): void {
    const item = this.itemToDelete;
    if (!item?.id || this.deleting()) {
      return;
    }

    this.deleting.set(true);

    this.mobileSettingsService
      .commonMobileSettingsDeleteIdDelete(item.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.deleting.set(false);
          this.deleteModalVisible.set(false);
          this.itemToDelete = null;
          this.showSuccess('adminMobileSettings.deleted');
          this.loadData();
        },
        error: (error: HttpErrorResponse) => {
          this.deleting.set(false);
          this.itemToDelete = null;
          this.showError('adminMobileSettings.deleteFailed', this.formatError(error));
        },
      });
  }

  // ----------------------------------------------------------------- table

  /** True when the row would force-update users that are already on the latest version. */
  private isGateInverted(
    minSupportedVersion: string | null | undefined,
    latestVersion: string | null | undefined,
  ): boolean {
    const min = this.parseSemVer(minSupportedVersion);
    const latest = this.parseSemVer(latestVersion);

    if (!min || !latest) {
      return false;
    }

    return this.compare(min, latest) > 0;
  }

  /** Mirrors AppVersionParser.TryParseSemVer so the UI shows the same verdict as the backend. */
  private parseSemVer(version: string | null | undefined): VersionTriple | null {
    if (!version?.trim()) {
      return null;
    }

    let core = version.trim();

    const plus = core.indexOf('+');
    if (plus >= 0) {
      core = core.slice(0, plus);
    }

    const dash = core.indexOf('-');
    if (dash >= 0) {
      core = core.slice(0, dash);
    }

    const parts = core.split('.');
    if (parts.length === 0 || parts.length > 3) {
      return null;
    }

    const numbers: VersionTriple = [0, 0, 0];
    for (let i = 0; i < parts.length; i++) {
      if (!/^\d+$/.test(parts[i])) {
        return null;
      }
      numbers[i] = Number(parts[i]);
    }

    return numbers;
  }

  private compare(a: VersionTriple, b: VersionTriple): number {
    for (let i = 0; i < 3; i++) {
      if (a[i] !== b[i]) {
        return a[i] - b[i];
      }
    }
    return 0;
  }

  // ----------------------------------------------------------------- utils

  private emptyForm(): MobileSettingsForm {
    return {
      id: undefined,
      platform: '',
      latestVersion: '',
      minSupportedVersion: '',
      storeUrl: '',
    };
  }

  private showSuccess(detailKey: string): void {
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('Success'),
      detail: this.translateService.instant(detailKey),
    });
  }

  private showError(summaryKey: string, detail: string): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant(summaryKey),
      detail,
      life: 8000,
    });
  }

  private formatError(error: HttpErrorResponse): string {
    const details = error.error;
    if (typeof details === 'string' && details.trim()) {
      return details;
    }
    if (details?.detail) {
      return details.detail;
    }
    if (details?.title) {
      return details.title;
    }
    return `${error.status || ''} ${error.statusText || 'Request failed'}`.trim();
  }
}
